import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import moment from "moment";
import Api from "api/Api";
import { getWebhookConfig, saveWebhookConfig } from "api/SpringApi";
import Toaster from "components/common/Toaster";

const TABS = ["Push Subscriptions", "Event Log", "Settings"];

const WebhookConfig = ({ storeId, storeName }) => {
  const [tab, setTab] = useState(0);
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newSecret, setNewSecret] = useState("");
  const [adding, setAdding] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [evtFilter, setEvtFilter] = useState("all");
  const [fallbackMinutes, setFallbackMinutes] = useState(30);
  const [existingConfig, setExistingConfig] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const toaster = useRef();

  useEffect(() => {
    if (storeId) {
      fetchSubs();
      fetchSettings();
    }
  }, [storeId]);
  useEffect(() => {
    if (storeId && tab === 1) fetchEvents();
  }, [tab, storeId]);

  const fetchSettings = () => {
    getWebhookConfig(storeId)
      .then(r => {
        const cfg = r.data.data;
        if (cfg) {
          setExistingConfig(cfg);
          if (cfg.fallbackMinutes) setFallbackMinutes(cfg.fallbackMinutes);
        }
      })
      .catch(() => {});
  };

  const handleSaveSettings = () => {
    if (!existingConfig || !existingConfig.webhookUrl) {
      toaster.current &&
        toaster.current.error(
          "No webhook URL configured yet. Add a Push Subscription first."
        );
      return;
    }
    setSettingsSaving(true);
    saveWebhookConfig(
      storeId,
      existingConfig.webhookUrl,
      existingConfig.webhookSecret,
      existingConfig.description,
      fallbackMinutes
    )
      .then(() => {
        toaster.current && toaster.current.success("Settings saved!");
        setSettingsSaving(false);
      })
      .catch(() => {
        toaster.current && toaster.current.error("Failed to save settings");
        setSettingsSaving(false);
      });
  };

  const fetchSubs = () => {
    setSubsLoading(true);
    Api.get(`admin/webhookConfig/subscriptions?storesId=${storeId}`)
      .then(r => {
        setSubs(r.data.data || []);
        setSubsLoading(false);
      })
      .catch(() => setSubsLoading(false));
  };

  const handleAdd = () => {
    if (!newName.trim() || !newUrl.trim()) {
      toaster.current && toaster.current.error("Name and URL are required");
      return;
    }
    // Append secret key to URL if provided — receiving end should validate this
    let finalUrl = newUrl.trim();
    if (newSecret.trim()) {
      const sep = finalUrl.includes("?") ? "&" : "?";
      finalUrl = `${finalUrl}${sep}greentill_key=${encodeURIComponent(
        newSecret.trim()
      )}`;
    }
    setAdding(true);
    const p = new URLSearchParams({
      storesId: storeId,
      name: newName,
      pushUrl: finalUrl
    });
    Api.post(`admin/webhookConfig/subscriptions/add?${p}`)
      .then(() => {
        toaster.current && toaster.current.success("Subscription added!");
        setNewName("");
        setNewUrl("");
        setNewSecret("");
        fetchSubs();
        setAdding(false);
      })
      .catch(e => {
        toaster.current &&
          toaster.current.error(e.response?.data?.message || "Failed to add");
        setAdding(false);
      });
  };

  const handleDelete = subscriptionId => {
    if (!window.confirm(`Delete subscription "${subscriptionId}"?`)) return;
    Api.delete(
      `admin/webhookConfig/subscriptions/remove?storesId=${storeId}&subscriptionId=${encodeURIComponent(
        subscriptionId
      )}`
    )
      .then(() => {
        toaster.current && toaster.current.success("Deleted");
        fetchSubs();
      })
      .catch(e =>
        toaster.current &&
          toaster.current.error(e.response?.data?.message || "Delete failed")
      );
  };

  const handleRecreateFilters = () => {
    if (
      !window.confirm(
        "Recreate this store's push subscriptions with a storeId filter? Unfiltered endpoints will stop receiving other stores' events."
      )
    ) {
      return;
    }
    Api.post(
      `admin/webhookConfig/subscriptions/recreate-filters?storesId=${storeId}`
    )
      .then(r => {
        const d = r.data.data || {};
        toaster.current &&
          toaster.current.success(
            `Recreated ${d.recreated || 0} subscription(s)` +
              (d.errors && d.errors.length
                ? `; ${d.errors.length} error(s)`
                : "")
          );
        fetchSubs();
      })
      .catch(e =>
        toaster.current &&
          toaster.current.error(
            e.response?.data?.message || "Failed to recreate filters"
          )
      );
  };

  const fetchEvents = () => {
    setEventsLoading(true);
    Api.get(`admin/parsedInvoice/list?storesId=${storeId}`)
      .then(r => {
        setEvents(r.data.data || []);
        setEventsLoading(false);
      })
      .catch(() => setEventsLoading(false));
  };

  const webhookBadge = ev => {
    if (!ev.webhookFired)
      return (
        <span style={{ color: "#6c757d", fontSize: 11 }}>— Not Fired</span>
      );
    const ok = ev.webhookStatus >= 200 && ev.webhookStatus < 300;
    return (
      <span
        style={{
          color: ok ? "#28a745" : "#dc3545",
          fontWeight: 600,
          fontSize: 11
        }}
      >
        {ok ? "✓" : "✗"} HTTP {ev.webhookStatus}
      </span>
    );
  };

  const filteredEvents = events.filter(ev => {
    if (evtFilter === "success")
      return (
        ev.webhookFired && ev.webhookStatus >= 200 && ev.webhookStatus < 300
      );
    if (evtFilter === "failed")
      return (
        ev.webhookFired && (ev.webhookStatus < 200 || ev.webhookStatus >= 300)
      );
    if (evtFilter === "pending") return !ev.webhookFired;
    return true;
  });

  return (
    <div className="roe-card-style mtb-15">
      <div className="roe-card-header module-header">
        <div className="flex-1 fs-16 demi-bold-text">
          <span className="hash"># </span>
          Event Integration {storeName ? `— ${storeName}` : ""}
        </div>
      </div>
      {/* Tabs */}
      <div className="roe-card-body" style={{ paddingTop: 0 }}>
        <div
          className="mb-0"
          style={{ borderBottom: "2px solid #e9ecef", display: "flex" }}
        >
          {TABS.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              style={{
                border: "none",
                background: "none",
                padding: "12px 24px",
                fontWeight: tab === i ? 700 : 400,
                borderBottom:
                  tab === i ? "2px solid #1a73e8" : "2px solid transparent",
                color: tab === i ? "#1a73e8" : "#666",
                cursor: "pointer",
                marginBottom: -2
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* ── TAB 0: Push Subscriptions ── */}
        {tab === 0 && (
          <div style={{ padding: "24px 0" }}>
            <div className="alert alert-success" style={{ fontSize: 13 }}>
              🔒 <strong>Store-scoped delivery</strong> — each push subscription
              only receives events for <strong>this store</strong> (Pub/Sub
              filter on <code>storeId</code>). Events are published to{" "}
              <strong>Cloud Pub/Sub (europe-west2)</strong>.
            </div>

            {/* Add form */}
            <div
              style={{
                background: "#f8f9fa",
                borderRadius: 8,
                padding: 20,
                marginBottom: 24
              }}
            >
              <h6 className="demi-bold-text mb-3">➕ Add Push Subscription</h6>
              <div className="row">
                <div className="col-md-3">
                  <label style={{ fontSize: 12, fontWeight: 600 }}>
                    Name *
                  </label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="e.g. zapier-crm"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                  />
                  <small className="text-muted">
                    Letters, numbers, hyphens only
                  </small>
                </div>
                <div className="col-md-4">
                  <label style={{ fontSize: 12, fontWeight: 600 }}>
                    Endpoint URL *
                  </label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="https://your-system.com/webhook"
                    value={newUrl}
                    onChange={e => setNewUrl(e.target.value)}
                  />
                  <small className="text-muted">
                    GCP POSTs invoice.processed JSON here
                  </small>
                </div>
                <div className="col-md-3">
                  <label style={{ fontSize: 12, fontWeight: 600 }}>
                    Secret Key{" "}
                    <span style={{ color: "#888", fontWeight: 400 }}>
                      (recommended)
                    </span>
                  </label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="e.g. my-secret-key-123"
                    value={newSecret}
                    onChange={e => setNewSecret(e.target.value)}
                  />
                  <small className="text-muted">
                    Added as <code>?greentill_key=…</code> — your endpoint
                    should validate this
                  </small>
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button
                    className="btn btn-primary btn-sm w-100"
                    onClick={handleAdd}
                    disabled={adding}
                  >
                    {adding ? "Adding…" : "Add"}
                  </button>
                </div>
              </div>
              <div
                className="alert alert-warning mt-3 mb-0"
                style={{ fontSize: 12 }}
              >
                🔑 <strong>Always use a Secret Key</strong> for production
                endpoints. Your receiving system should check for{" "}
                <code>?greentill_key=your-key</code> and reject requests without
                it. For Zapier, add a filter step that checks this value.
              </div>
            </div>

            {/* Subscriptions table */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12
              }}
            >
              <h6 className="demi-bold-text mb-0">Active Push Subscriptions</h6>
              {subs.some(s => !s.storeScoped) && (
                <button
                  className="btn btn-warning btn-sm"
                  onClick={handleRecreateFilters}
                >
                  Fix: apply store filters
                </button>
              )}
            </div>
            {subs.some(s => !s.storeScoped) && (
              <div className="alert alert-warning" style={{ fontSize: 12 }}>
                One or more subscriptions have <strong>no store filter</strong>{" "}
                and may receive events from other stores. Click{" "}
                <strong>Fix: apply store filters</strong> to recreate them.
              </div>
            )}
            {subsLoading ? (
              <p className="text-muted">Loading…</p>
            ) : subs.length === 0 ? (
              <div className="alert alert-info" style={{ fontSize: 13 }}>
                No push subscriptions yet. Add one above to start receiving
                events in an external system.
              </div>
            ) : (
              <table className="table table-bordered table-sm">
                <thead style={{ background: "#f1f3f4" }}>
                  <tr>
                    <th>Name</th>
                    <th>Endpoint URL</th>
                    <th>Scope</th>
                    <th>Ack Deadline</th>
                    <th style={{ width: 80 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map(s => (
                    <tr key={s.subscriptionId}>
                      <td>
                        <code style={{ fontSize: 12 }}>{s.subscriptionId}</code>
                      </td>
                      <td style={{ wordBreak: "break-all", fontSize: 13 }}>
                        {s.pushUrl || <span className="text-muted">—</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {s.storeScoped ? (
                          <span style={{ color: "#28a745", fontWeight: 600 }}>
                            This store only
                          </span>
                        ) : (
                          <span style={{ color: "#dc3545", fontWeight: 600 }}>
                            All stores (unfiltered)
                          </span>
                        )}
                      </td>
                      <td>{s.ackDeadline}s</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(s.subscriptionId)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <button
              className="btn btn-outline-secondary btn-sm mt-2"
              onClick={fetchSubs}
            >
              ↻ Refresh
            </button>
          </div>
        )}

        {/* ── TAB 1: Event Log ── */}
        {tab === 1 && (
          <div style={{ padding: "24px 0" }}>
            {/* Header + filter + refresh */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12
              }}
            >
              <h6 className="demi-bold-text mb-0">Invoice Webhook Events</h6>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={fetchEvents}
              >
                ↻ Refresh
              </button>
            </div>

            {/* Filter pills */}
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 14,
                flexWrap: "wrap"
              }}
            >
              {[
                { key: "all", label: `All (${events.length})` },
                {
                  key: "success",
                  label: `✓ Delivered (${
                    events.filter(
                      e =>
                        e.webhookFired &&
                        e.webhookStatus >= 200 &&
                        e.webhookStatus < 300
                    ).length
                  })`
                },
                {
                  key: "failed",
                  label: `✗ Failed (${
                    events.filter(
                      e =>
                        e.webhookFired &&
                        (e.webhookStatus < 200 || e.webhookStatus >= 300)
                    ).length
                  })`
                },
                {
                  key: "pending",
                  label: `— Not Fired (${
                    events.filter(e => !e.webhookFired).length
                  })`
                }
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setEvtFilter(f.key)}
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: 20,
                    padding: "3px 14px",
                    fontSize: 12,
                    cursor: "pointer",
                    background: evtFilter === f.key ? "#1a73e8" : "#fff",
                    color: evtFilter === f.key ? "#fff" : "#555",
                    fontWeight: evtFilter === f.key ? 600 : 400
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {eventsLoading ? (
              <p className="text-muted">Loading events…</p>
            ) : filteredEvents.length === 0 ? (
              <div className="alert alert-info" style={{ fontSize: 13 }}>
                No events yet. Events appear here after a customer scans a QR
                code and submits their email.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  className="table table-bordered table-sm"
                  style={{ fontSize: 12 }}
                >
                  <thead style={{ background: "#f1f3f4" }}>
                    <tr>
                      <th>Parsed At</th>
                      <th>Webhook Status</th>
                      <th>Fired At</th>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th>Amount</th>
                      <th>Salesperson</th>
                      <th>Parser</th>
                      <th>Confidence</th>
                      <th>Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.map((ev, i) => (
                      <React.Fragment key={ev.parsedInvoiceId || i}>
                        <tr
                          style={{
                            cursor: ev.webhookResponse ? "pointer" : "default"
                          }}
                          onClick={() =>
                            ev.webhookResponse &&
                            setExpandedRow(expandedRow === i ? null : i)
                          }
                        >
                          <td style={{ whiteSpace: "nowrap" }}>
                            {ev.parsedAt
                              ? moment(ev.parsedAt).format("DD/MM/YY HH:mm:ss")
                              : "—"}
                          </td>
                          <td>{webhookBadge(ev)}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {ev.webhookFiredAt ? (
                              moment(ev.webhookFiredAt).format(
                                "DD/MM/YY HH:mm:ss"
                              )
                            ) : (
                              <span style={{ color: "#aaa" }}>—</span>
                            )}
                          </td>
                          <td>
                            <strong>{ev.orderNumber || "—"}</strong>
                          </td>
                          <td>
                            <div>{ev.billName || "—"}</div>
                            <div style={{ color: "#888", fontSize: 11 }}>
                              {ev.customerEmail || ""}
                            </div>
                          </td>
                          <td>{ev.amount ? `€${ev.amount}` : "—"}</td>
                          <td>{ev.salesperson || "—"}</td>
                          <td>{ev.parserSource || "—"}</td>
                          <td>
                            {ev.confidence != null
                              ? `${Math.round(ev.confidence * 100)}%`
                              : "—"}
                          </td>
                          <td>
                            {ev.webhookResponse ? (
                              <span style={{ color: "#1a73e8" }}>
                                {expandedRow === i ? "▲ Hide" : "▼ Show"}
                              </span>
                            ) : (
                              <span style={{ color: "#aaa" }}>—</span>
                            )}
                          </td>
                        </tr>
                        {expandedRow === i && ev.webhookResponse && (
                          <tr>
                            <td
                              colSpan={10}
                              style={{ background: "#f8f9fa", padding: 12 }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  marginBottom: 6,
                                  color: "#555"
                                }}
                              >
                                <strong>URL:</strong> {ev.webhookUrl}
                                &nbsp;|&nbsp;
                                <strong>HTTP:</strong> {ev.webhookStatus}
                                &nbsp;|&nbsp;
                                <strong>Fired:</strong>{" "}
                                {ev.webhookFiredAt
                                  ? moment(ev.webhookFiredAt).format(
                                      "DD/MM/YYYY HH:mm:ss"
                                    )
                                  : "—"}
                              </div>
                              <pre
                                style={{
                                  fontSize: 11,
                                  margin: 0,
                                  maxHeight: 250,
                                  overflow: "auto",
                                  background: "#fff",
                                  padding: 8,
                                  border: "1px solid #dee2e6",
                                  borderRadius: 4
                                }}
                              >
                                {(() => {
                                  try {
                                    return JSON.stringify(
                                      JSON.parse(ev.webhookResponse),
                                      null,
                                      2
                                    );
                                  } catch {
                                    return ev.webhookResponse;
                                  }
                                })()}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── TAB 2: Settings ── */}
        {tab === 2 && (
          <div style={{ padding: "24px 0", maxWidth: 480 }}>
            <h6 className="demi-bold-text mb-1">⏱ Fallback Parse Window</h6>
            <p style={{ fontSize: 13, color: "#555", marginBottom: 20 }}>
              If a customer <strong>does not scan the QR code</strong> within
              this window, the invoice is automatically parsed and sent to your
              webhook without customer consent.
            </p>

            <div className="form-group">
              <label style={{ fontSize: 13, fontWeight: 600 }}>
                Fallback window (minutes)
              </label>
              <div className="input-group" style={{ maxWidth: 200 }}>
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  max={1440}
                  value={fallbackMinutes}
                  onChange={e => setFallbackMinutes(Number(e.target.value))}
                />
                <div className="input-group-append">
                  <span className="input-group-text">min</span>
                </div>
              </div>
              <small className="text-muted">
                Default: 30 min. Range: 1 – 1440 min (24 h).
              </small>
            </div>

            <button
              className="btn btn-primary btn-sm mt-2"
              onClick={handleSaveSettings}
              disabled={settingsSaving}
            >
              {settingsSaving ? "Saving…" : "Save Settings"}
            </button>
          </div>
        )}
      </div>
      <Toaster ref={toaster} />
    </div>
  );
};

const mapStateToProps = state => ({
  ...state.themeChanger,
  ...state.storeID
});

export default connect(mapStateToProps)(WebhookConfig);
